/**
 * Single-slot serializer for passive Word read probes.
 *
 * The addin fires several independent Word.run() reads on the same UI
 * events — empty-doc check + cursor-section detection on selection
 * settle, plus a state-transition empty-doc check. Office.js will
 * queue them internally, but firing them concurrently from React lets
 * the queue grow unbounded under rapid input, and makes failure modes
 * (one Word.run rejecting while another races behind it) hard to
 * reason about. Routing the reads through one promise chain keeps
 * them strictly sequential without needing a third-party limiter.
 *
 * Intentionally NOT used for draft writes / section commits — those
 * have their own ordering in wordWriter and are not in scope here.
 */
import { deferUntilLiveDocSettled, isLiveDocLoading } from "./liveDocLoadState";
import { isWordWriteQueueBusy, whenWordWriteQueueIdle } from "./wordWriteQueue";

let tail: Promise<unknown> = Promise.resolve();

/**
 * Longest a read holds the queue. A Word.run that never settles (seen on Word
 * desktop) used to wedge every passive read behind it for the rest of the
 * session — the scope chip stuck on "document". Past this the next read starts;
 * the slow read's own promise still settles (or not) for its caller unchanged.
 */
export const WORD_READ_SLOT_TIMEOUT_MS = 15_000;

export function queueWordRead<T>(label: string, fn: () => Promise<T>): Promise<T> {
  let releaseSlot: () => void = () => {};
  const slot = new Promise<void>((resolve) => {
    releaseSlot = resolve;
  });
  const start = () => {
    // The clock starts when the read runs, not while it waits its turn.
    const timer = setTimeout(() => {
      console.warn(`[wordReadQueue] "${label}" held the read queue >${WORD_READ_SLOT_TIMEOUT_MS} ms — releasing it`);
      releaseSlot();
    }, WORD_READ_SLOT_TIMEOUT_MS);
    const run = Promise.resolve().then(fn);
    void run.then(
      () => {
        clearTimeout(timer);
        releaseSlot();
      },
      () => {
        clearTimeout(timer);
        releaseSlot();
      },
    );
    return run;
  };
  const next = tail.then(start, start);
  tail = slot;
  return next;
}

/**
 * Wrap an async task so overlapping calls share the run already in flight
 * instead of starting (and queueing) another. For periodic / event-driven
 * refreshes — e.g. the cursor-section poll — whose latest answer is all that
 * matters: without it, a probe slower than its trigger interval piles up in
 * ``queueWordRead`` without bound, and every read behind it (a chip click,
 * the empty-doc check) waits longer the longer the session runs.
 */
const parkedReads = new Set<string>();

/**
 * For a passive read about to start: when a Load or a queued write owns Word,
 * park ``replay`` and return true (the caller stands down); otherwise return
 * false and the caller reads now. The selection fan-out is already held while
 * Word is busy, but debounced listeners fire up to 1.5 s later — often inside
 * the next write of a run — and a read queued behind it only lengthens the
 * stall, then answers stale. Parked once per ``key``; ``replay`` runs when Word
 * is idle again (and re-checks, so a write that started meanwhile parks it
 * again), so the UI the read feeds still catches up.
 */
export function parkWhileWordBusy(key: string, replay: () => void): boolean {
  const loading = isLiveDocLoading();
  if (!loading && !isWordWriteQueueBusy()) return false;
  if (parkedReads.has(key)) return true;
  parkedReads.add(key);
  const fire = () => {
    parkedReads.delete(key);
    replay();
  };
  if (loading) deferUntilLiveDocSettled(`parkedRead:${key}`, fire);
  else whenWordWriteQueueIdle(fire);
  return true;
}

/** Test hook — forget parked reads between cases. */
export function __resetParkedReadsForTests(): void {
  parkedReads.clear();
}

export function coalesceInFlight<T>(fn: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null;
  return () => {
    if (inFlight) return inFlight;
    const run = fn().finally(() => {
      inFlight = null;
    });
    inFlight = run;
    return run;
  };
}
